// Sugalarm — kiosk-side pairing surface.
//
// Unauthenticated cold-boot: the Pi boots, hits /, has no token, and lands
// here. We POST /api/pairing/request, render the 6-digit code, and poll
// /api/pairing/poll/{id} every 2s. On `approved`, the response carries
// `device_token` (one-shot) and we persist it under sugalarm.deviceToken
// before navigating to /now.
//
// Cycle 175 — FORCE-DISPLAY mode (`forceDisplay`): reached from Settings
// on an ALREADY-paired kiosk ("Pair this screen"). A paired kiosk is a
// signed-in (device-token) session, so App's authenticated /pair route
// renders this screen for it instead of redirecting to /now — letting the
// kiosk OFFER a fresh code (re-link). In this mode we add a back/cancel
// control (the cycle-162 glass BackButton) that returns to /now so the
// kiosk is never stranded. The cold-boot path (no prop) is byte-identical
// to before: no back control. Re-approval from a phone re-mints the token
// onto the SAME devices row (cycle-131 client_device_id, sent below).
//
// Cycle 331 — reused verbatim for the onboarding `/pair-device` entry (a
// tappable card on the CGM-provider screen, letting a device being set
// up mirror a household already signed in elsewhere instead of connecting
// a fresh CGM). That route always passes `forceDisplay` + `backTo="/login"`
// so the back control returns to the provider step rather than the
// default "/now" (which may not exist yet without a CGM linked).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import * as pairingApi from "../../api/pairing";
import { BackButton } from "../../components/ui/BackButton";
import { Logo } from "../../components/ui/Logo";
import { useFrameVariant } from "../../hooks/useFrameVariant";
import { getOrCreateClientId, setDeviceToken, setKiosk } from "../../lib/auth";
import {
  isAndroidTabletClassRuntime,
  isIPadClassRuntime,
  isPhoneLikeRuntime,
} from "../../lib/deviceClass";
import styles from "./KioskPairingScreen.module.css";
import { PAIR_DEVICE_PATH } from "../../lib/pairDevicePath";

type Phase = "loading" | "showing" | "expired" | "error";

const POLL_MS_DEFAULT = 2_000;
const REQUEST_BACKOFF_INITIAL = 1_000;
const REQUEST_BACKOFF_CAP = 30_000;

interface State {
  pairingId: string | null;
  code: string | null;
  expiresAt: number | null;
  phase: Phase;
  attempt: number;
  errorMessage: string | null;
}

const initialState: State = {
  pairingId: null,
  code: null,
  expiresAt: null,
  phase: "loading",
  attempt: 0,
  errorMessage: null,
};

function jitter(base: number): number {
  return base + Math.random() * 200;
}

export function KioskPairingScreen({
  forceDisplay = false,
  backTo = "/now",
  backLabel = "Now",
  fitFrame,
}: {
  /** Cycle 175: reached from Settings on a paired kiosk — render the
   *  back/cancel control. The cold-boot path leaves this false (no back). */
  forceDisplay?: boolean;
  /** Cycle 331: override the back control's destination + label. Every
   *  existing `forceDisplay` caller (cycle 175/220/221 relink) omits
   *  these and keeps the default "/now"/"Now" — byte-identical. The new
   *  onboarding `/pair-device` entry passes `backTo="/login"` (there is
   *  no Now screen yet without a CGM) so the back control returns to the
   *  CGM-provider step instead. */
  backTo?: string;
  backLabel?: string;
  /** Cycle 452.1: the signed-in routes render this screen INSIDE the kiosk
   *  frame (below the header, beside the rail, above any bottom strip such
   *  as the No Data banner). There the older (non-tablet) layout fills the
   *  space it is given instead of the full screen height, which pushed the
   *  frame's No Data banner below the fold on the 1024×600 kiosk. The
   *  signed-out routes render outside the frame and keep `100vh`. The
   *  tablet takeover (`nativeEntry`) is unaffected. REQUIRED, no default:
   *  every caller decides — `fitFrame={variant === "kiosk"}` inside the
   *  frame, `fitFrame={false}` for the signed-out routes. */
  fitFrame: boolean;
}) {
  const nav = useNavigate();
  // Cycle 220: the Android-tablet kiosk gets a full-screen pairing takeover.
  // The gate is the same one cycles 199–207 use — kiosk variant AND
  // `isAndroidTabletClassRuntime()` (the `nativeEntry` predicate). When
  // false, the existing inline-styled layout renders byte-identically (Pi /
  // iPad / web / mobile unchanged). The pairing hooks/effects below are
  // untouched — this cycle only changes what is drawn, not how pairing works.
  const variant = useFrameVariant();
  const nativeEntry = variant === "kiosk" && isAndroidTabletClassRuntime();
  // The caller that picks the frame decides (`fitFrame={variant === "kiosk"}`):
  // only the kiosk frame's `.kioskContent` is a flex column this can fill;
  // phone and web keep the full-height layout they had.
  const fit = fitFrame;
  const [s, setS] = useState<State>(initialState);
  const [now, setNow] = useState<number>(() => Date.now());
  const requestBackoffRef = useRef(REQUEST_BACKOFF_INITIAL);
  // Cycle 463.3: at most ONE code request in flight and at most ONE retry
  // pending. The expiry effect re-runs every second (it depends on `now`),
  // and while a request kept failing `expiresAt` never moved, so each second
  // started another retry chain that never ended. On 2026-10-12 one tablet
  // built thousands of them during a database outage and then sent ~28
  // requests a second at the server.
  const requestInFlightRef = useRef(false);
  const retryTimerRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (retryTimerRef.current !== null) {
        window.clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    },
    [],
  );

  // Persist the kiosk flag once we land here so future cold boots default
  // to /pair without needing the ?kiosk=1 query param again. Cycle 39:
  // skip the persist when the runtime is phone-like — without this
  // guard a phone that lands on /pair (via misclicked link, autofill,
  // etc.) would re-introduce the stuck-on-kiosk bug `useFrameVariant`'s
  // auto-clear path is designed to recover from. Cycle 193: a tablet
  // (iPad or Android tablet) ALSO satisfies `isPhoneLikeRuntime()`, so
  // narrow the guard to a phone-that-is-not-a-tablet — otherwise a tablet
  // could never complete kiosk pairing. The cycle-39 protection for real
  // phones is unchanged.
  useEffect(() => {
    if (
      isPhoneLikeRuntime() &&
      !isIPadClassRuntime() &&
      !isAndroidTabletClassRuntime()
    ) {
      return;
    }
    setKiosk(true);
  }, []);

  // Tick once a second so the countdown ring updates.
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(id);
  }, []);

  // Issue a fresh code. Retries with exponential backoff on network failures.
  const requestCode = useCallback(async () => {
    // A request or a scheduled retry already owns the next code.
    if (requestInFlightRef.current || retryTimerRef.current !== null) return;
    requestInFlightRef.current = true;
    setS((prev) => ({ ...prev, phase: "loading", errorMessage: null }));
    let r: Awaited<ReturnType<typeof pairingApi.createPairing>>;
    try {
      r = await pairingApi.createPairing({
        requested_device_name: "Kitchen kiosk",
        requested_device_type: "kiosk",
        // Cycle 131: present this device's stable, persisted client id so a
        // re-pair lands back on the existing devices row instead of minting
        // a new one. (If this Chromium profile is ephemeral the id won't
        // survive a reboot — that's a Pi-side concern, see CHANGELOG 131.)
        client_device_id: getOrCreateClientId(),
      });
    } finally {
      requestInFlightRef.current = false;
    }
    if (!r.ok) {
      const delay = Math.min(requestBackoffRef.current, REQUEST_BACKOFF_CAP);
      requestBackoffRef.current = Math.min(
        requestBackoffRef.current * 2,
        REQUEST_BACKOFF_CAP,
      );
      setS((prev) => ({
        ...prev,
        phase: "error",
        errorMessage: r.error.message,
      }));
      retryTimerRef.current = window.setTimeout(() => {
        retryTimerRef.current = null;
        void requestCode();
      }, jitter(delay));
      return;
    }
    requestBackoffRef.current = REQUEST_BACKOFF_INITIAL;
    setS({
      pairingId: r.data.pairing_id,
      code: r.data.code,
      expiresAt: new Date(r.data.expires_at).getTime(),
      phase: "showing",
      attempt: 0,
      errorMessage: null,
    });
  }, []);

  useEffect(() => {
    void requestCode();
  }, [requestCode]);

  // Poll loop. Lives on the pairingId so a fresh code resets the loop.
  useEffect(() => {
    if (!s.pairingId) return;
    let cancelled = false;

    const tick = async (): Promise<void> => {
      if (cancelled) return;
      const r = await pairingApi.pollPairing(s.pairingId!);
      if (cancelled) return;
      if (!r.ok) {
        // 404 means the row got reaped; just rotate to a new code.
        if (r.error.kind === "not_found") {
          await requestCode();
          return;
        }
        // 429 / network: try again on the next tick.
        window.setTimeout(tick, jitter(POLL_MS_DEFAULT));
        return;
      }
      const out = r.data;
      if (out.status === "approved" && out.device_token && out.device_id) {
        setDeviceToken(out.device_token, out.device_id);
        // Hard reload so useAuth re-hydrates from the freshly-stored
        // device token (a soft nav() would re-render with the cached
        // signedOut state and bounce us back to /pair, looping on a
        // fresh code instead of switching to the live view).
        window.location.replace("/now");
        return;
      }
      if (out.status === "expired" || out.status === "revoked") {
        await requestCode();
        return;
      }
      // pending or consumed-by-someone-else (shouldn't happen for our id);
      // keep polling at the documented cadence.
      window.setTimeout(tick, POLL_MS_DEFAULT);
    };

    window.setTimeout(tick, POLL_MS_DEFAULT);
    return () => {
      cancelled = true;
    };
  }, [s.pairingId, requestCode]);

  // Rotate codes on TTL expiry.
  useEffect(() => {
    if (!s.expiresAt) return;
    if (now < s.expiresAt) return;
    void requestCode();
  }, [now, s.expiresAt, requestCode]);

  const code = s.code ?? "······";
  const codeFormatted = useMemo(() => {
    const a = code.slice(0, 3);
    const b = code.slice(3, 6);
    return { a, b };
  }, [code]);

  const totalSeconds = s.expiresAt
    ? Math.max(0, Math.floor((s.expiresAt - now) / 1000))
    : 0;

  // Progress bar runs from full → empty over the default 10-minute TTL.
  // SUGALARM_PAIRING_TTL_SECONDS can override the server's TTL, but the
  // default is what every operator runs; the bar saturates at full if a
  // longer TTL is configured, which is fine for the visual.
  const PAIRING_TTL_S = 600;
  const fraction =
    s.expiresAt && totalSeconds > 0
      ? Math.max(0, Math.min(1, totalSeconds / PAIRING_TTL_S))
      : 0;

  // Cycle 220: Android-tablet full-screen takeover. Same code value/state +
  // expiry/rotation source as the inline path — nothing is re-fetched or
  // re-minted; this is presentation only.
  if (nativeEntry) {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    const mmss = `${mins}:${String(secs).padStart(2, "0")}`;
    return (
      <div className={styles.androidTablet} data-testid="pairing-screen">
        <div className={styles.glow} />

        {/* Cycle 175 relink only (a paired kiosk re-linking from Settings):
            a glass back control so the kiosk is never stranded. Absent on
            the cold-boot path. ≥44px target via the shared BackButton. */}
        {forceDisplay ? (
          <div className={styles.backControl}>
            <BackButton label={backLabel} onBack={() => nav(backTo)} />
          </div>
        ) : null}

        <div className={styles.brand}>
          <Logo size={64} />
          <span className={styles.wordmark}>Sugar Alarm</span>
        </div>

        <div className={styles.eyebrow}>Pair this screen</div>
        <h1 className={styles.lead}>Enter this code on your phone to pair</h1>

        <div className={styles.code}>
          <div className={styles.codeGroup}>
            {codeFormatted.a.split("").map((d, i) => (
              <span key={`a${i}`} className={styles.codeCell}>
                {d}
              </span>
            ))}
          </div>
          <div className={styles.codeGroup}>
            {codeFormatted.b.split("").map((d, i) => (
              <span key={`b${i}`} className={styles.codeCell}>
                {d}
              </span>
            ))}
          </div>
        </div>

        <div className={styles.countdown}>
          <div
            className={styles.bar}
            role="progressbar"
            aria-label="Code rotation countdown"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(fraction * 100)}
          >
            <div
              className={styles.barFill}
              style={{ transform: `scaleX(${fraction})` }}
            />
          </div>
          <div className={styles.countdownLine}>
            {s.phase === "error" ? (
              <span className={styles.warn}>
                Reconnecting… {s.errorMessage ?? ""}
              </span>
            ) : s.phase === "loading" ? (
              "Generating code…"
            ) : (
              <>
                This code expires in <span className="tnum">{mmss}</span> · a new
                one appears automatically
              </>
            )}
          </div>
        </div>

        <div className={styles.steps}>
          <div className={styles.step}>
            <span className={styles.stepNum}>1</span>
            <span className={styles.stepText}>Open Sugar Alarm on your phone</span>
          </div>
          <span className={styles.stepArrow}>→</span>
          <div className={styles.step}>
            <span className={styles.stepNum}>2</span>
            <span className={styles.stepText}>
              Go to {PAIR_DEVICE_PATH}
            </span>
          </div>
          <span className={styles.stepArrow}>→</span>
          <div className={styles.step}>
            <span className={styles.stepNum}>3</span>
            <span className={styles.stepText}>Type the code above</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="pairing-screen"
      style={{
        position: "relative",
        ...(fit
          ? { flex: "1 1 0", minHeight: 0, overflow: "hidden" }
          : { minHeight: "100vh" }),
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: fit ? "24px 36px" : "32px 36px 64px",
        background: "var(--bg)",
        color: "var(--ink)",
        gap: 24,
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(circle at 50% 30%, var(--accent-soft), transparent 55%)",
          pointerEvents: "none",
        }}
      />

      {/* Cycle 175: force-display only (an already-paired kiosk re-linking
          from Settings) — a glass back control so the kiosk is never
          stranded on the code screen. Absent on the cold-boot path. */}
      {forceDisplay ? (
        <div
          style={{ position: "absolute", top: 20, left: 20, zIndex: 2 }}
        >
          <BackButton label={backLabel} onBack={() => nav(backTo)} />
        </div>
      ) : null}

      <div
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <Logo size={88} />
        <span style={{ fontWeight: 700, fontSize: 28, letterSpacing: "-0.005em" }}>
          Sugar Alarm
        </span>
      </div>

      <div
        style={{
          position: "relative",
          textAlign: "center",
          maxWidth: 520,
        }}
      >
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: "0.10em",
            textTransform: "uppercase",
            color: "var(--ink-3)",
            marginBottom: 6,
          }}
        >
          Pair this kiosk
        </div>
        <h1
          style={{
            margin: 0,
            fontSize: 22,
            fontWeight: 600,
            letterSpacing: "-0.02em",
          }}
        >
          On your phone, sign in to Sugar Alarm and enter this code under{" "}
          <span style={{ color: "var(--accent)" }}>{PAIR_DEVICE_PATH}</span>.
        </h1>
      </div>

      <div
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        <CodeBlock value={codeFormatted.a} />
        <span
          style={{
            width: 16,
            height: 1.5,
            background: "var(--ink-3)",
            opacity: 0.6,
          }}
        />
        <CodeBlock value={codeFormatted.b} />
      </div>

      <CountdownBar fraction={fraction} />

      <div
        style={{
          fontSize: "var(--type-floor)",
          color: "var(--ink-3)",
          textAlign: "center",
          maxWidth: 480,
          position: "relative",
        }}
      >
        {s.phase === "error" ? (
          <span style={{ color: "var(--zone-warn)" }}>
            Reconnecting… {s.errorMessage ?? ""}
          </span>
        ) : s.phase === "loading" ? (
          "Generating code…"
        ) : (
          <>
            Code rotates in <span className="tnum">{totalSeconds}s</span>. The kiosk
            will switch to your live glucose view as soon as your phone approves.
          </>
        )}
      </div>
    </div>
  );
}

function CodeBlock({ value }: { value: string }) {
  return (
    <span
      className="tnum"
      style={{
        display: "inline-flex",
        gap: 8,
        padding: "12px 14px",
        background: "var(--bg-elev)",
        borderRadius: 14,
        border: "0.5px solid var(--line-strong)",
        boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
      }}
    >
      {value.split("").map((d, i) => (
        <span
          key={i}
          style={{
            fontFamily: "var(--hero-font)",
            fontWeight: 600,
            fontSize: 96,
            lineHeight: 0.86,
            letterSpacing: "-0.04em",
            color: "var(--ink)",
            minWidth: "0.7em",
            textAlign: "center",
          }}
        >
          {d}
        </span>
      ))}
    </span>
  );
}

function CountdownBar({ fraction }: { fraction: number }) {
  return (
    <div
      role="progressbar"
      aria-label="Code rotation countdown"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(fraction * 100)}
      style={{
        position: "relative",
        width: 360,
        height: 6,
        borderRadius: 999,
        background: "var(--line)",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          width: "100%",
          height: "100%",
          background: "var(--accent)",
          opacity: 0.85,
          transformOrigin: "left center",
          transform: `scaleX(${fraction})`,
          transition: "transform 1s linear",
        }}
      />
    </div>
  );
}
